import type { ReactNode } from 'react';
import { createContext, useContext, useLayoutEffect, useRef } from 'react';

export interface StickyHeaderContextType {
  stickyHeader: ReactNode | null;
  setStickyHeader: (content: ReactNode | null) => void;
  register: () => () => void;
}

export const StickyHeaderContext = createContext<StickyHeaderContextType | null>(null);

export function useStickyHeader(content: ReactNode | null): void {
  const context = useContext(StickyHeaderContext);
  const unregisterRef = useRef<(() => void) | null>(null);

  useLayoutEffect((): undefined | (() => void) => {
    if (context === null) return undefined;
    unregisterRef.current = context.register();
    return (): void => {
      unregisterRef.current?.();
      unregisterRef.current = null;
    };
  }, [context]);

  useLayoutEffect((): void => {
    context?.setStickyHeader(content);
  }, [content, context]);
}

export function useStickyHeaderContent(): ReactNode | null {
  const context = useContext(StickyHeaderContext);
  return context?.stickyHeader ?? null;
}
